import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft,
  Bell,
  Moon,
  Sun,
  Shield,
  Volume2,
  Network,
  Smartphone,
  Trash2,
  LogOut,
  TestTube,
  Languages
} from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNotifications } from '@/hooks/mensag/useNotifications';
import { LanguageSelector } from '@/components/mensag/LanguageSelector';
import { useNavigate } from 'react-router-dom';

export default function SettingsPage() {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { user } = useCurrentUser();
  const { permissionStatus, requestPermission, showTestNotification, isSupported } = useNotifications();

  const [notifications, setNotifications] = useState({
    messages: true,
    calls: true,
    groups: true,
    silent: false,
  });

  const [privacy, setPrivacy] = useState({
    readReceipts: true,
    onlineStatus: true,
    profilePhoto: true,
    lastSeen: true,
  });

  const [media, setMedia] = useState({
    autoDownload: true,
    saveToGallery: false,
    backupMedia: true,
  });

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 md:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="md:hidden"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <ArrowLeft className="h-5 w-5 hidden md:inline" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Configuración
            </h2>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Account Info */}
          {user && (
            <Card>
              <CardHeader>
                <CardTitle>Cuenta</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{user.profile?.name || 'Usuario'}</p>
                    <p className="text-sm text-gray-500">Ver y editar perfil</p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/profile')}
                  >
                    Gestionar
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notifications */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Bell className="h-5 w-5" />
                <span>Notificaciones Push</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Estado de notificaciones</Label>
                  <p className="text-sm text-gray-500">
                    {permissionStatus === 'granted' ? 'Activadas' :
                     permissionStatus === 'denied' ? 'Bloqueadas' :
                     permissionStatus === 'default' ? 'No solicitadas' : 'Desconocido'}
                  </p>
                </div>
                <Button
                  onClick={requestPermission}
                  disabled={!isSupported || permissionStatus === 'granted'}
                  variant={permissionStatus === 'granted' ? 'secondary' : 'default'}
                  className="px-3 py-1 text-sm"
                >
                  {permissionStatus === 'granted' ? 'Activadas' :
                   permissionStatus === 'denied' ? 'Bloqueadas' : 'Activar'}
                </Button>
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  onClick={showTestNotification}
                  disabled={permissionStatus !== 'granted'}
                  size="sm"
                  className="flex items-center space-x-2"
                >
                  <TestTube className="h-4 w-4" />
                  <span>Probar notificación</span>
                </Button>
                <p className="text-xs text-gray-500">
                  Envía una notificación de prueba
                </p>
              </div>

              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <p>• Recibe notificaciones de solicitudes de contacto</p>
                <p>• Avisos de mensajes nuevos cuando la app esté cerrada</p>
                <p>• Recordatorios de conversaciones pendientes</p>
              </div>
            </CardContent>
          </Card>

          {/* Language */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Languages className="h-5 w-5" />
                <span>Idioma / Language</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <LanguageSelector />
              <p className="text-xs text-gray-500 mt-2">
                Las traducciones están disponibles en 10 idiomas incluyendo español, inglés, portugués y más.
              </p>
            </CardContent>
          </Card>

          {/* Appearance */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Sun className="h-5 w-5" />
                <span>Apariencia</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Tema</Label>
                  <p className="text-sm text-gray-500">Elige el tema de la aplicación</p>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant={theme === 'light' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTheme('light')}
                  >
                    <Sun className="h-4 w-4 mr-2" />
                    Claro
                  </Button>
                  <Button
                    variant={theme === 'dark' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTheme('dark')}
                  >
                    <Moon className="h-4 w-4 mr-2" />
                    Oscuro
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Privacy */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Shield className="h-5 w-5" />
                <span>Privacidad</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="read-receipts">Confirmaciones de lectura</Label>
                  <p className="text-sm text-gray-500">Mostrar cuando leo mensajes</p>
                </div>
                <Switch
                  id="read-receipts"
                  checked={privacy.readReceipts}
                  onCheckedChange={(checked) =>
                    setPrivacy(prev => ({ ...prev, readReceipts: checked }))
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="online-status">Estado en línea</Label>
                  <p className="text-sm text-gray-500">Mostrar cuando estoy conectado</p>
                </div>
                <Switch
                  id="online-status"
                  checked={privacy.onlineStatus}
                  onCheckedChange={(checked) =>
                    setPrivacy(prev => ({ ...prev, onlineStatus: checked }))
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="profile-photo">Foto de perfil</Label>
                  <p className="text-sm text-gray-500">Quién puede ver mi foto</p>
                </div>
                <Switch
                  id="profile-photo"
                  checked={privacy.profilePhoto}
                  onCheckedChange={(checked) =>
                    setPrivacy(prev => ({ ...prev, profilePhoto: checked }))
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="last-seen">Última vez visto</Label>
                  <p className="text-sm text-gray-500">Mostrar cuando estuve activo</p>
                </div>
                <Switch
                  id="last-seen"
                  checked={privacy.lastSeen}
                  onCheckedChange={(checked) =>
                    setPrivacy(prev => ({ ...prev, lastSeen: checked }))
                  }
                />
              </div>
            </CardContent>
          </Card>

          {/* Account Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-red-600">Acciones de Cuenta</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button variant="outline" className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50">
                <Trash2 className="h-4 w-4 mr-2" />
                Limpiar todo el historial
              </Button>
              <Button variant="outline" className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50">
                <LogOut className="h-4 w-4 mr-2" />
                Cerrar sesión
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}